'use client';

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { MessageSquare, Plus, Trash2, Users, UserCog, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMarkMessagesSeen } from '@/components/notification-bell';
import { cn } from '@/lib/utils';

const audienceLabels: Record<string, { label: string; icon: typeof Users; color: string }> = {
  all_parents: { label: 'All Parents', icon: Users, color: 'bg-green-100 text-green-700' },
  all_coaches: { label: 'All Coaches', icon: UserCog, color: 'bg-blue-100 text-blue-700' },
  all_members: { label: 'Everyone', icon: Megaphone, color: 'bg-purple-100 text-purple-700' },
  session_attendees: { label: 'Session', icon: Users, color: 'bg-amber-100 text-amber-700' },
};

const priorityColors: Record<string, string> = {
  low: 'border-l-gray-300',
  normal: 'border-l-blue-400',
  high: 'border-l-amber-500',
  urgent: 'border-l-red-500',
};

export default function DirectorAnnouncementsPage() {
  useMarkMessagesSeen('director');

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetAudience: 'all_parents',
    priority: 'normal',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const announcementsData = useQuery(api.director.announcements.list, { limit: 50 });
  const announcements = announcementsData?.announcements ?? [];
  const total = announcementsData?.pagination?.total;
  const isLoading = announcementsData === undefined;

  const createAnnouncementMutation = useMutation(api.director.announcements.create);
  const deleteAnnouncementMutation = useMutation(api.director.announcements.remove);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createAnnouncementMutation({
        title: formData.title,
        content: formData.content,
        targetAudience: formData.targetAudience as 'all_parents' | 'all_coaches' | 'all_members' | 'session_attendees',
        priority: formData.priority as 'low' | 'normal' | 'high' | 'urgent',
      });
      setShowForm(false);
      setFormData({ title: '', content: '', targetAudience: 'all_parents', priority: 'normal' });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create announcement');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-y-2">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Announcements</h1>
            <p className="text-sm text-muted-foreground">{total ?? 0} announcements</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus className="w-4 h-4 mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-card rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Create Announcement</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Announcement title..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Message</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                rows={4}
                placeholder="Write your announcement..."
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Target Audience</label>
                <select
                  value={formData.targetAudience}
                  onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                  className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="all_parents">All Parents</option>
                  <option value="all_coaches">All Coaches</option>
                  <option value="all_members">Everyone</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Announcement'}
              </Button>
            </div>
            {submitError && (
              <p className="text-red-500 text-sm">{submitError}</p>
            )}
          </form>
        </div>
      )}

      {/* Announcements List */}
      <div className="space-y-4">
        {announcements.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-8 text-center">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No announcements yet</p>
            <p className="text-sm text-muted-foreground">Create your first announcement to get started</p>
          </div>
        ) : (
          <>
            {announcements.map((announcement) => {
              const audience = audienceLabels[announcement.targetAudience] || audienceLabels.all_members;
              const AudienceIcon = audience.icon;
              return (
                <div
                  key={announcement.id}
                  className={cn(
                    'bg-card rounded-lg shadow p-4 border-l-4',
                    priorityColors[announcement.priority] || priorityColors.normal
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-foreground">{announcement.title}</h3>
                        <span className={cn('px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1', audience.color)}>
                          <AudienceIcon className="w-3 h-3" />
                          {audience.label}
                        </span>
                        {announcement.priority === 'urgent' && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            Urgent
                          </span>
                        )}
                        {announcement.priority === 'high' && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                            High Priority
                          </span>
                        )}
                      </div>
                      <p className="text-foreground whitespace-pre-wrap">{announcement.content}</p>
                      <div className="mt-3 text-xs text-muted-foreground">
                        {announcement.createdByEmail ? <span>By {announcement.createdByEmail} · </span> : null}
                        {new Date(announcement.createdAt).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm('Delete this announcement?')) {
                          deleteAnnouncementMutation({ announcementId: announcement.id });
                        }
                      }}
                      className="text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
